import type { ReactElement, ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useAdminSession } from '@wlb/shared/context/AdminSessionContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../../components/ui/Button';

interface BootScreenProps {
  title: string;
  message?: string;
  actions?: ReactNode;
  alert?: boolean;
}

function BootScreen({ title, message, actions, alert = false }: BootScreenProps) {
  return (
    <main className="app-boot">
      <div className="app-boot__card" role={alert ? 'alert' : 'status'}>
        <h1 className="app-boot__title">{title}</h1>
        {message ? <p>{message}</p> : null}
        {actions ? <div className="app-boot__actions">{actions}</div> : null}
      </div>
    </main>
  );
}

/**
 * Route guard for every admin route family: an anonymous visitor goes to
 * /login, a signed-in non-admin gets a dead end with a sign-out action, and
 * only a confirmed admin role reaches the panel.
 */
export function RequireAdmin() {
  const { t } = useTranslation('admin');
  const { initializing: authInitializing, isAuthenticated, logout } = useAuth();
  const { initializing, isAdmin, accessDenied, error, recheck } = useAdminSession();
  const location = useLocation();

  if (authInitializing) {
    return <BootScreen title={t('common.loading')} />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (initializing) {
    return <BootScreen title={t('session.verifying')} />;
  }

  if (accessDenied) {
    return (
      <BootScreen
        alert
        title={t('session.deniedTitle')}
        message={t('session.deniedMessage')}
        actions={
          <Button variant="secondary" onClick={logout}>
            {t('session.signOut')}
          </Button>
        }
      />
    );
  }

  if (error) {
    return (
      <BootScreen
        alert
        title={t('session.errorTitle')}
        message={error}
        actions={
          <>
            <Button onClick={recheck}>{t('session.retry')}</Button>
            <Button variant="secondary" onClick={logout}>
              {t('session.signOut')}
            </Button>
          </>
        }
      />
    );
  }

  if (!isAdmin) {
    // If we reach here, the role check finished without a clear result.
    // This should not happen in normal operation, but showing the verifying
    // spinner forever would be a dead end — surface it as an error instead.
    return (
      <BootScreen
        alert
        title={t('session.errorTitle')}
        message={t('session.roleCheckFailed')}
        actions={
          <>
            <Button onClick={recheck}>{t('session.retry')}</Button>
            <Button variant="secondary" onClick={logout}>
              {t('session.signOut')}
            </Button>
          </>
        }
      />
    );
  }

  return <Outlet />;
}

/** Keeps a signed-in admin out of the login screen. */
export function AnonymousOnly({ children }: { children: ReactElement }) {
  const { initializing, isAuthenticated } = useAuth();
  const { t } = useTranslation('admin');
  const location = useLocation();

  if (initializing) {
    return <BootScreen title={t('common.loading')} />;
  }
  if (!isAuthenticated) {
    return children;
  }
  const from = (location.state as { from?: string } | null)?.from;
  return <Navigate to={from ?? '/'} replace />;
}
